import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Cloud, Check, AlertCircle, RefreshCw, Settings, 
  ExternalLink, Shield, Trash2, Plus, CheckCircle, XCircle, Plug, Database
} from 'lucide-react';
import { toast } from 'sonner';

// Import connect components
import AWSConnect, { useAWSCredentials } from '../components/AWSConnect';
import AzureConnect, { useAzureCredentials } from '../components/AzureConnect';
import GCPConnect, { useGCPCredentials } from '../components/GCPConnect';
import SnowflakeConnect, { useSnowflakeCredentials } from '../components/SnowflakeConnect';

// Import cloud logos
import { AWSLogo, AzureLogo, GCPLogo, SnowflakeLogo } from '../components/CloudLogos';
import { useDemoData } from '../components/useDemoData';
import { useSelectedProviders } from '../hooks/useSelectedProviders';
import EmptyDataState from '../components/EmptyDataState';

const CloudIntegrations = () => {
  const { shouldShowData } = useDemoData();
  const { providers: selectedIds, shouldShowAll } = useSelectedProviders();

  // Use credential hooks for each provider
  const awsCreds = useAWSCredentials();
  const azureCreds = useAzureCredentials();
  const gcpCreds = useGCPCredentials();
  const snowflakeCreds = useSnowflakeCredentials();

  // State for which connect dialog is open
  const [showConnect, setShowConnect] = useState(null);

  // Integration definitions
  const integrations = [
    { 
      id: 'aws', 
      name: 'Amazon Web Services', 
      LogoComponent: AWSLogo,
      bgColor: 'bg-orange-100 bg-gradient-to-br from-orange-100 to-orange-50 dark:from-orange-900/30 dark:to-orange-950/20',
      borderColor: 'border-orange-200 dark:border-orange-800',
      isConnected: awsCreds.isConnected,
      disconnect: awsCreds.disconnect,
      description: 'Cost Explorer API for real-time cost analysis',
      features: ['Cost by Service', 'Daily Trends', 'Forecasting', 'Top Resources']
    },
    { 
      id: 'azure', 
      name: 'Microsoft Azure', 
      LogoComponent: AzureLogo,
      bgColor: 'bg-blue-100 bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/30 dark:to-blue-950/20',
      borderColor: 'border-emerald-200 dark:border-blue-800',
      isConnected: azureCreds.isConnected,
      disconnect: azureCreds.disconnect,
      description: 'Cost Management API for Azure subscriptions',
      features: ['Cost by Service', 'Daily Trends', 'Forecasting', 'Regional Costs']
    },
    { 
      id: 'gcp', 
      name: 'Google Cloud Platform', 
      LogoComponent: GCPLogo,
      bgColor: 'bg-red-100 bg-gradient-to-br from-red-100 to-yellow-50 dark:from-red-900/30 dark:to-yellow-950/20',
      borderColor: 'border-red-200 dark:border-red-800',
      isConnected: gcpCreds.isConnected,
      disconnect: gcpCreds.disconnect,
      description: 'Cloud Billing API for GCP projects',
      features: ['Billing Accounts', 'Service Catalog', 'BigQuery Export Guide']
    },
    { 
      id: 'snowflake', 
      name: 'Snowflake', 
      LogoComponent: SnowflakeLogo,
      bgColor: 'bg-cyan-100 bg-gradient-to-br from-cyan-100 to-cyan-50 dark:from-cyan-900/30 dark:to-cyan-950/20',
      borderColor: 'border-emerald-200 dark:border-cyan-800',
      isConnected: snowflakeCreds.isConnected,
      disconnect: snowflakeCreds.disconnect,
      description: 'Direct query access to Snowflake cost data',
      features: ['Credit Usage', 'Warehouse Metering', 'Query Costs', 'User Attribution']
    }
  ];

  const handleConnected = (provider, credentials, accountInfo) => {
    setShowConnect(null);
    toast.success(`Connected to ${provider.toUpperCase()} successfully!`);
  };

  const handleDisconnect = (integration) => {
    integration.disconnect();
    toast.success(`${integration.name} disconnected`);
  };

  const getConnectedCount = () => {
    return integrations.filter(i => i.isConnected).length;
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Cloud Integrations" description="Connect your AWS / GCP / Azure account to see your real cost data here." />;
  }


  return (
    <div className="space-y-6" data-testid="cloud-integrations-page">
      {/* Connect Dialogs */}
      <AWSConnect 
        isOpen={showConnect === 'aws'} 
        onClose={() => setShowConnect(null)}
        onConnected={(creds, info) => handleConnected('aws', creds, info)}
      />
      <AzureConnect 
        isOpen={showConnect === 'azure'} 
        onClose={() => setShowConnect(null)}
        onConnected={(creds, info) => handleConnected('azure', creds, info)}
      />
      <GCPConnect 
        isOpen={showConnect === 'gcp'} 
        onClose={() => setShowConnect(null)}
        onConnected={(creds, info) => handleConnected('gcp', creds, info)}
      />
      <SnowflakeConnect 
        isOpen={showConnect === 'snowflake'} 
        onClose={() => setShowConnect(null)}
        onConnected={(creds, info) => handleConnected('snowflake', creds, info)}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Cloud className="h-7 w-7 text-emerald-500" />
            Cloud Integrations
          </h1>
          <p className="text-slate-400 dark:text-gray-400 mt-1">
            Connect your cloud accounts to get real-time cost data
          </p>
        </div>
        <div className="flex items-center gap-2">
          {getConnectedCount() > 0 && (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              <CheckCircle className="w-3 h-3 mr-1" />
              {getConnectedCount()} Connected
            </Badge>
          )}
        </div>
      </div>

      {/* Connection Summary */}
      {getConnectedCount() > 0 && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                <Database className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">Live Cost Data Available</p>
                <p className="text-sm text-slate-400 dark:text-slate-400">
                  Your connected accounts are ready. Go to the dashboard to view real-time costs.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Integration Cards — split into "Your Clouds" + "More Providers" */}
      {(() => {
        const yourClouds = shouldShowAll
          ? integrations
          : integrations.filter((i) => selectedIds.includes(i.id));
        const moreClouds = shouldShowAll
          ? []
          : integrations.filter((i) => !selectedIds.includes(i.id));

        return (
          <>
            {yourClouds.length > 0 && (
              <div>
                {!shouldShowAll && (
                  <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3" data-testid="your-clouds-label">
                    Your Clouds
                  </p>
                )}
                <div className="grid gap-6 md:grid-cols-2">
                  {yourClouds.map((integration) => (
                    <Card
                      key={integration.id}
                      className={`relative overflow-hidden transition-all ${
                        integration.isConnected
                          ? 'border-emerald-200 dark:border-emerald-800 shadow-emerald-100 dark:shadow-emerald-900/20'
                          : 'hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                      data-testid={`integration-${integration.id}`}
                    >
                      {integration.isConnected && (
                        <div className="absolute top-0 right-0 px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-bl-lg flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          Connected
                        </div>
                      )}
                      <CardHeader>
                        <div className="flex items-center gap-4">
                          <div className={`w-16 h-16 ${integration.bgColor} ${integration.borderColor} border rounded-xl flex items-center justify-center p-3 shadow-sm`}>
                            <integration.LogoComponent className="w-full h-full" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{integration.name}</CardTitle>
                            <CardDescription className="text-sm">
                              {integration.description}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4">
                          <p className="text-xs text-slate-400 mb-2">Available Features:</p>
                          <div className="flex flex-wrap gap-1">
                            {integration.features.map((feature, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {feature}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        {integration.isConnected ? (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                              <Check className="w-4 h-4" />
                              <span>Receiving live cost data</span>
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowConnect(integration.id)}>
                                <Settings className="w-4 h-4 mr-1" />
                                Settings
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                onClick={() => handleDisconnect(integration)}
                                data-testid={`disconnect-${integration.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            className="w-full bg-emerald-600 hover:bg-emerald-700"
                            onClick={() => setShowConnect(integration.id)}
                            data-testid={`connect-${integration.id}`}
                          >
                            <Plug className="w-4 h-4 mr-2" />
                            Connect Account
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {moreClouds.length > 0 && (
              <div className="mt-8">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3" data-testid="more-clouds-label">
                  More supported providers
                </p>
                <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                  {moreClouds.map((integration) => (
                    <button
                      key={integration.id}
                      onClick={() => setShowConnect(integration.id)}
                      className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-emerald-400 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-all text-left group"
                      data-testid={`more-integration-${integration.id}`}
                    >
                      <div className={`w-10 h-10 ${integration.bgColor} rounded-lg flex items-center justify-center p-1.5 shrink-0`}>
                        <integration.LogoComponent className="w-full h-full" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{integration.name}</p>
                        <p className="text-[11px] text-slate-400 group-hover:text-emerald-600 transition-colors">+ Add</p>
                      </div>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 mt-3">
                  Tip: You can also update your cloud selection in{' '}
                  <a href="/settings" className="text-emerald-600 hover:underline font-medium">Settings → Your Clouds</a>.
                </p>
              </div>
            )}
          </>
        );
      })()}

      {/* Security Section */}
      <Card className="bg-slate-50 dark:bg-slate-800/50 border-dashed">
        <CardContent className="py-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Your Data Never Leaves Your Infrastructure</h3>
              <p className="text-sm text-slate-400 dark:text-slate-400 mb-4">
                CARTIEAI uses a <strong>stateless architecture</strong>. Your cloud credentials are stored only in your browser 
                and passed directly to your cloud provider's API. We <strong>never</strong> store your credentials or cost data on our servers.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Zero Credential Storage</p>
                    <p className="text-slate-400 text-xs">Credentials stay in your browser</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Direct API Queries</p>
                    <p className="text-slate-400 text-xs">Data flows provider → you</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Read-Only Access</p>
                    <p className="text-slate-400 text-xs">We can't modify your resources</p>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                <Button variant="outline" size="sm" asChild>
                  <a href="/security" className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    View Security Documentation
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Setup Help */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Need Help Setting Up?</CardTitle>
          <CardDescription>
            Follow our guides to create the required credentials for each cloud provider.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <a 
              href="https://docs.aws.amazon.com/IAM/latest/UserGuide/id_credentials_access-keys.html"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="w-10 h-10 bg-orange-50 dark:bg-orange-900/20 rounded-lg p-2 flex-shrink-0">
                <AWSLogo className="w-full h-full" />
              </div>
              <div>
                <p className="font-medium text-sm">AWS IAM Setup</p>
                <p className="text-xs text-slate-400">Create access keys</p>
              </div>
              <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
            </a>
            <a 
              href="https://docs.microsoft.com/en-us/azure/active-directory/develop/howto-create-service-principal-portal"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="w-10 h-10 bg-emerald-50 dark:bg-blue-900/20 rounded-lg p-2 flex-shrink-0">
                <AzureLogo className="w-full h-full" />
              </div>
              <div>
                <p className="font-medium text-sm">Azure Service Principal</p>
                <p className="text-xs text-slate-400">Create app registration</p>
              </div>
              <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
            </a>
            <a 
              href="https://cloud.google.com/iam/docs/keys-create-delete"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-lg p-2 flex-shrink-0">
                <GCPLogo className="w-full h-full" />
              </div>
              <div>
                <p className="font-medium text-sm">GCP Service Account</p>
                <p className="text-xs text-slate-400">Create key file</p>
              </div>
              <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
            </a>
            <a 
              href="https://docs.snowflake.com/en/user-guide/admin-account-identifier"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-lg border hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="w-10 h-10 bg-emerald-50 dark:bg-cyan-900/20 rounded-lg p-2 flex-shrink-0">
                <SnowflakeLogo className="w-full h-full" />
              </div>
              <div>
                <p className="font-medium text-sm">Snowflake Setup</p>
                <p className="text-xs text-slate-400">Find account identifier</p>
              </div>
              <ExternalLink className="w-4 h-4 ml-auto text-slate-400" />
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CloudIntegrations;
